import { UTILITIES_ENABLED } from '@/lib/features'

// Enhanced category colors for visual organization — Nordic Sanctuary palette
// Uses semantic tokens that adapt to light/dark mode automatically
export const categoryColors = {
  electronics: {
    bg: 'bg-info/12',
    text: 'text-info',
    border: 'border-info/20',
  },
  appliances: {
    bg: 'bg-success/12',
    text: 'text-success',
    border: 'border-success/20',
  },
  furniture: {
    bg: 'bg-primary/12',
    text: 'text-primary',
    border: 'border-primary/20',
  },
  hvac: {
    bg: 'bg-warning/15',
    text: 'text-warning',
    border: 'border-warning/20',
  },
  plumbing: {
    bg: 'bg-info/12',
    text: 'text-info',
    border: 'border-info/20',
  },
  lighting: {
    bg: 'bg-warning/15',
    text: 'text-warning',
    border: 'border-warning/20',
  },
  security: {
    bg: 'bg-destructive/12',
    text: 'text-destructive',
    border: 'border-destructive/20',
  },
  garden: {
    bg: 'bg-success/12',
    text: 'text-success',
    border: 'border-success/20',
  },
  tools: {
    bg: 'bg-primary/12',
    text: 'text-primary',
    border: 'border-primary/20',
  },
  other: {
    bg: 'bg-muted/50',
    text: 'text-muted-foreground',
    border: 'border-border',
  },
} as const

// Extended color palette for dynamic category generation — Nordic Sanctuary
// Cycles through the 4 semantic status colors + primary to create variety
// while staying within the palette. Each uses accessible text tokens from DESIGN.md.
export const extendedCategoryColors = [
  {
    name: 'Sage',
    bg: 'bg-success/12',
    text: 'text-success',
    border: 'border-success/20',
    hover: 'hover:bg-success/18',
  },
  {
    name: 'Slate',
    bg: 'bg-info/12',
    text: 'text-info',
    border: 'border-info/20',
    hover: 'hover:bg-info/18',
  },
  {
    name: 'Amber',
    bg: 'bg-warning/15',
    text: 'text-warning',
    border: 'border-warning/20',
    hover: 'hover:bg-warning/22',
  },
  {
    name: 'Terracotta',
    bg: 'bg-destructive/12',
    text: 'text-destructive',
    border: 'border-destructive/20',
    hover: 'hover:bg-destructive/18',
  },
  {
    name: 'Primary',
    bg: 'bg-primary/12',
    text: 'text-primary',
    border: 'border-primary/20',
    hover: 'hover:bg-primary/18',
  },
  {
    name: 'Oatmeal',
    bg: 'bg-secondary/60',
    text: 'text-foreground',
    border: 'border-secondary',
    hover: 'hover:bg-secondary/80',
  },
  {
    name: 'Sage Deep',
    bg: 'bg-success/18',
    text: 'text-success',
    border: 'border-success/25',
    hover: 'hover:bg-success/25',
  },
  {
    name: 'Slate Deep',
    bg: 'bg-info/18',
    text: 'text-info',
    border: 'border-info/25',
    hover: 'hover:bg-info/25',
  },
  {
    name: 'Amber Deep',
    bg: 'bg-warning/22',
    text: 'text-warning',
    border: 'border-warning/28',
    hover: 'hover:bg-warning/28',
  },
  {
    name: 'Terracotta Deep',
    bg: 'bg-destructive/18',
    text: 'text-destructive',
    border: 'border-destructive/25',
    hover: 'hover:bg-destructive/25',
  },
  {
    name: 'Muted',
    bg: 'bg-muted/50',
    text: 'text-muted-foreground',
    border: 'border-border',
    hover: 'hover:bg-muted/70',
  },
  {
    name: 'Primary Deep',
    bg: 'bg-primary/18',
    text: 'text-primary',
    border: 'border-primary/25',
    hover: 'hover:bg-primary/25',
  },
  {
    name: 'Sage Light',
    bg: 'bg-success/8',
    text: 'text-success',
    border: 'border-success/15',
    hover: 'hover:bg-success/12',
  },
  {
    name: 'Amber Light',
    bg: 'bg-warning/10',
    text: 'text-warning',
    border: 'border-warning/15',
    hover: 'hover:bg-warning/15',
  },
  {
    name: 'Slate Light',
    bg: 'bg-info/8',
    text: 'text-info',
    border: 'border-info/15',
    hover: 'hover:bg-info/12',
  },
] as const

// Status colors for maintenance tasks — Nordic Sanctuary semantic tokens
export const statusColors = {
  pending: {
    bg: 'bg-warning/15',
    text: 'text-warning',
    border: 'border-warning/20',
  },
  in_progress: {
    bg: 'bg-info/12',
    text: 'text-info',
    border: 'border-info/20',
  },
  completed: {
    bg: 'bg-success/12',
    text: 'text-success',
    border: 'border-success/20',
  },
  overdue: {
    bg: 'bg-destructive/12',
    text: 'text-destructive',
    border: 'border-destructive/20',
  },
} as const

// Condition colors for items — Nordic Sanctuary semantic tokens
export const conditionColors = {
  excellent: {
    bg: 'bg-success/12',
    text: 'text-success',
    border: 'border-success/20',
  },
  good: {
    bg: 'bg-info/12',
    text: 'text-info',
    border: 'border-info/20',
  },
  fair: {
    bg: 'bg-warning/15',
    text: 'text-warning',
    border: 'border-warning/20',
  },
  poor: {
    bg: 'bg-destructive/12',
    text: 'text-destructive',
    border: 'border-destructive/20',
  },
} as const

// Utility type colors — Nordic Sanctuary semantic tokens
export const utilityColors = {
  electricity: {
    bg: 'bg-warning/15',
    text: 'text-warning',
    border: 'border-warning/20',
  },
  gas: {
    bg: 'bg-destructive/12',
    text: 'text-destructive',
    border: 'border-destructive/20',
  },
  water: {
    bg: 'bg-info/12',
    text: 'text-info',
    border: 'border-info/20',
  },
  internet: {
    bg: 'bg-primary/12',
    text: 'text-primary',
    border: 'border-primary/20',
  },
  trash: {
    bg: 'bg-success/12',
    text: 'text-success',
    border: 'border-success/20',
  },
  sewer: {
    bg: 'bg-info/18',
    text: 'text-info',
    border: 'border-info/25',
  },
  other: {
    bg: 'bg-muted/50',
    text: 'text-muted-foreground',
    border: 'border-border',
  },
} as const

// Default categories for items
export const itemCategories = [
  'Electronics',
  'Appliances',
  'Furniture',
  'HVAC',
  'Plumbing',
  'Lighting',
  'Security',
  'Garden',
  'Tools',
  'Other',
] as const

// Default categories for parts
export const partCategories = [
  'Filters',
  'Fasteners',
  'Electrical',
  'Plumbing',
  'HVAC Parts',
  'Cleaning Supplies',
  'Lubricants',
  'Consumables',
  'Hardware',
  'Other',
] as const

// File size limits
export const fileLimits = {
  image: 10 * 1024 * 1024, // 10MB
  document: 25 * 1024 * 1024, // 25MB
} as const

// Supported file types
export const supportedImageTypes = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
] as const

export const supportedDocumentTypes = ['application/pdf', 'text/plain'] as const

// Navigation — grouped per DESIGN.md (3 primary + secondary tier)
export const navGroups = [
  {
    label: 'Home',
    items: [
      { title: 'Dashboard', href: '/dashboard', icon: 'LayoutDashboard' },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { title: 'Items', href: '/items', icon: 'Package' },
      { title: 'Parts', href: '/parts', icon: 'Wrench' },
    ],
  },
  {
    label: 'Maintenance',
    items: [
      { title: 'Maintenance', href: '/maintenance', icon: 'ClipboardList' },
      ...(UTILITIES_ENABLED
        ? ([{ title: 'Utilities', href: '/utilities', icon: 'Zap' }] as const)
        : []),
    ],
  },
] as const

export const navSecondary = [
  { title: 'Properties', href: '/properties', icon: 'Building2' },
] as const

// Flat list for backwards compat (mobile nav, page title lookup, etc.)
export const navItems = [
  ...navGroups.flatMap((g) => [...g.items]),
  ...navSecondary,
]

// Recurring intervals for maintenance tasks
export const recurringIntervals = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Bi-weekly', value: 'biweekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Semi-annually', value: 'semi-annual' },
  { label: 'Annually', value: 'annual' },
]

export const RECURRING_INTERVAL_VALUES = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'semi-annual',
  'annual',
] as const

export type RecurringInterval = (typeof RECURRING_INTERVAL_VALUES)[number]

/**
 * Spellings that mean one of the canonical intervals but aren't it. Anything
 * stored outside RECURRING_INTERVAL_VALUES silently falls through recurrence
 * math (addInterval) to a 30-day fallback, so a task labelled "semi-annual"
 * would reschedule one month out. Keys are lowercase + trimmed.
 */
const RECURRING_INTERVAL_ALIASES: Record<string, RecurringInterval> = {
  'every day': 'daily',
  'each day': 'daily',
  'every week': 'weekly',
  'each week': 'weekly',
  'bi-weekly': 'biweekly',
  'bi weekly': 'biweekly',
  fortnightly: 'biweekly',
  'every 2 weeks': 'biweekly',
  'every two weeks': 'biweekly',
  'every month': 'monthly',
  'each month': 'monthly',
  'every 3 months': 'quarterly',
  'every three months': 'quarterly',
  'every quarter': 'quarterly',
  semiannual: 'semi-annual',
  'semi annual': 'semi-annual',
  'semi-annually': 'semi-annual',
  'semi annually': 'semi-annual',
  semiannually: 'semi-annual',
  biannual: 'semi-annual',
  'bi-annual': 'semi-annual',
  'twice a year': 'semi-annual',
  'every 6 months': 'semi-annual',
  'every six months': 'semi-annual',
  annually: 'annual',
  yearly: 'annual',
  'every year': 'annual',
  'each year': 'annual',
  'once a year': 'annual',
}

/** Values that explicitly mean "not recurring". */
const NON_RECURRING_VALUES = new Set(['', 'none', 'one-time', 'one time', 'n/a'])

/**
 * Map any interval spelling to a canonical value. Returns null for empty,
 * "one-time"-style and unrecognizable input.
 */
export function normalizeRecurringInterval(
  value: string | null | undefined
): RecurringInterval | null {
  if (!value || typeof value !== 'string') return null

  const lower = value.trim().toLowerCase()
  if (NON_RECURRING_VALUES.has(lower)) return null
  if (RECURRING_INTERVAL_VALUES.includes(lower as RecurringInterval)) {
    return lower as RecurringInterval
  }
  return RECURRING_INTERVAL_ALIASES[lower] ?? null
}

export const MAINTENANCE_STATUSES = [
  'pending',
  'in_progress',
  'completed',
  'overdue',
] as const

export const UTILITY_TYPES = [
  'electricity',
  'gas',
  'water',
  'internet',
  'trash',
  'sewer',
  'other',
] as const

export const UTILITY_USAGE_UNITS = [
  'kwh',
  'therms',
  'ccf',
  'gallons',
  'cubic_feet',
  'mbps',
  'gb',
  'channels',
  'lines',
  'tons',
  'cubic_yards',
  'units',
  'dollars',
] as const

// Storage and date-related constants
export const SIGNED_URL_EXPIRY_DAYS = 100000
export const SIGNED_URL_EXPIRY_SECONDS = SIGNED_URL_EXPIRY_DAYS * 24 * 60 * 60

/**
 * Lifetime of the short-lived signed URLs /api/images mints (nothing stored).
 * Clients caching one must refetch before it runs out.
 */
export const IMAGE_ROUTE_SIGNED_URL_TTL_SECONDS = 60 * 60

export const UPCOMING_TASKS_DAYS_THRESHOLD = 15
export const UPCOMING_TASKS_MS =
  UPCOMING_TASKS_DAYS_THRESHOLD * 24 * 60 * 60 * 1000

// User authentication retry configuration
export const AUTH_MAX_RETRIES = 3
export const AUTH_RETRY_DELAY_MS = 100
